// TMOS User Position Assignment Modal
// Date: December 19, 2025
// Purpose: Modal for assigning positions to users

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { PositionSelector } from './PositionSelector';
import { userPositionService, UserPositionAssignment } from '@/services/userPositionService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { UserPosition } from '@/services/businessPositionService';

interface UserPositionAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userEmail: string;
  existingPositions?: UserPosition[];
  onSuccess?: () => void;
}

export const UserPositionAssignmentModal: React.FC<UserPositionAssignmentModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  userEmail,
  existingPositions = [],
  onSuccess
}) => {
  const { t } = useLanguage();
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
  const [primaryPositionId, setPrimaryPositionId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Initialize with existing positions
      const existingIds = existingPositions.map(p => p.position_id);
      setSelectedPositionIds(existingIds);
      const primary = existingPositions.find(p => p.is_primary);
      setPrimaryPositionId(primary?.position_id);
    }
  }, [isOpen, existingPositions]);

  const handleSave = async () => {
    if (selectedPositionIds.length === 0) {
      toast.error(t('position.selectAtLeastOne') || 'Please select at least one position');
      return;
    }

    try {
      setSaving(true);

      // Get current positions to determine what to add/remove
      const currentPositions = await userPositionService.getUserPositions(userId);
      const currentPositionIds = currentPositions.map(p => p.position_id);

      // Positions to add
      const positionsToAdd = selectedPositionIds.filter(id => !currentPositionIds.includes(id));
      // Positions to remove
      const positionsToRemove = currentPositionIds.filter(id => !selectedPositionIds.includes(id));

      // Remove positions that are no longer selected
      for (const positionId of positionsToRemove) {
        await userPositionService.removePositionFromUser(userId, positionId);
      }

      // Add new positions
      if (positionsToAdd.length > 0) {
        const assignment: UserPositionAssignment = {
          userId,
          positionIds: positionsToAdd,
          primaryPositionId: primaryPositionId && positionsToAdd.includes(primaryPositionId)
            ? primaryPositionId
            : undefined
        };
        await userPositionService.assignPositionsToUser(userId, assignment);
      }

      // Update primary position if changed
      if (primaryPositionId) {
        const currentPrimary = currentPositions.find(p => p.is_primary);
        if (currentPrimary?.position_id !== primaryPositionId) {
          await userPositionService.setPrimaryPosition(primaryPositionId);
        }
      }

      toast.success(t('position.assignmentsUpdated') || 'Position assignments updated successfully');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving position assignments:', error);
      toast.error(error.message || t('position.updateError') || 'Failed to update position assignments');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setSelectedPositionIds([]);
      setPrimaryPositionId(undefined);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('position.assignPositions') || 'Assign Positions'}
      size="lg"
    >
      <div className="space-y-6">
        {/* User Info */}
        <div className="p-4 bg-[var(--color-secondary-bg)] rounded-lg">
          <div className="text-sm font-medium text-[var(--color-surface-text)]">
            {t('position.assigningTo') || 'Assigning positions to'}
          </div>
          <div className="mt-1">
            <div className="font-semibold text-[var(--color-surface-text)]">
              {userName}
            </div>
            <div className="text-sm text-[var(--color-secondary-text)]">
              {userEmail}
            </div>
          </div>
        </div>

        {/* Position Selector */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
            {t('position.selectPositions') || 'Select Positions'}
          </label>
          <PositionSelector
            selectedPositionIds={selectedPositionIds}
            onSelectionChange={setSelectedPositionIds}
            primaryPositionId={primaryPositionId}
            onPrimaryChange={setPrimaryPositionId}
            multiSelect={true}
            showCategories={true}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={selectedPositionIds.length === 0}
          >
            {t('common.save') || 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

